/**
 * التقاط معرّف نقرة Meta (`fbclid`) وبناء `fbc`/`fbp`.
 *
 * لماذا: الموقع على lecmo.com لكنّ الدفع على checkout شوبيفاي، فلا تصل
 * جلسة المتصفّح إلى لحظة الشراء. نحفظ `fbclid` عند وصول الزائر، ونقرأ
 * كوكي `_fbp` الذي ينشئه البكسل، ثمّ نمرّرهما كسمات سلّة مخفيّة →
 * `note_attributes` في Shopify → أعمدة في جدول `orders`. لاحقاً يستخدمها
 * Purchase عبر Conversions API لتحسين Event Match Quality.
 *
 * صيغة `fbc` كما تطلبها Meta: `fb.1.<timestamp_ms>.<fbclid>`
 * (الرقم 1 = subdomain index لدومين من مستوى واحد مثل lecmo.com).
 *
 * كلّ الدوالّ SSR-safe.
 *
 * Consent: nothing is stored or forwarded without current explicit ads
 * consent. A first-landing click before any choice is kept in memory only;
 * an explicit grant persists it, rejection/withdrawal clears memory and
 * storage, and a withdrawn click is never revived afterwards.
 */

import { hasAdsConsent, readConsent } from "@/lib/cookie-consent";
import { adsWithdrawnSince, currentConsentGeneration, onAdsWithdrawn } from "@/lib/consentTransitions";
export { META_CLICK_ATTR_KEYS } from "@/lib/metaClickKeys";

const STORAGE_KEY = "lecmo:meta_click";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // ٧ أيّام — نافذة النقرة المعتمدة لدى Meta

export interface MetaClickId {
  fbclid: string;
  /** fbc جاهز بصيغة Meta */
  fbc: string;
  /** ISO timestamp للنقرة */
  clickAt: string;
  expires: number;
}

function isValidFbclid(value: string | null): value is string {
  if (!value) return false;
  const v = value.trim();
  return v.length > 0 && v.length <= 512;
}

/** يبني fbc من fbclid وزمن النقرة (ميلي ثانية). */
export function buildFbc(fbclid: string, timestampMs: number): string {
  return `fb.1.${timestampMs}.${fbclid}`;
}

/** يقرأ كوكي `_fbp` الذي ينشئه البكسل نفسه (لا نصنعه يدويّاً). */
export function getFbp(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
    const value = match?.[1] ? decodeURIComponent(match[1]).trim() : "";
    return value.length > 0 && value.length <= 256 ? value : null;
  } catch {
    return null;
  }
}

/** In-memory click captured before the visitor's first consent choice. */
let pendingClick: (MetaClickId & { gen: number }) | null = null;

/** Clears all Meta-owned click state (memory + storage). */
export function clearMetaClickId(): void {
  pendingClick = null;
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
}

// Synchronous withdrawal: clears memory even before delayed handlers run.
onAdsWithdrawn(clearMetaClickId);

/**
 * Call on every consent change: a grant persists a still-pending landing
 * click only if no withdrawal was recorded after it was captured; anything
 * else clears Meta-owned click state for good.
 */
export function syncMetaClickConsent(): void {
  if (typeof window === "undefined") return;
  if (!hasAdsConsent()) {
    clearMetaClickId();
    return;
  }
  const pending = pendingClick;
  pendingClick = null;
  if (pending && !adsWithdrawnSince(pending.gen) && pending.expires >= Date.now()) {
    const { gen: _gen, ...click } = pending;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(click));
    } catch {
      /* storage blocked */
    }
  }
}

/** يقرأ النقرة المحفوظة (أو null إن انتهت صلاحيّتها أو غابت الموافقة). */
export function getMetaClickId(): MetaClickId | null {
  if (typeof window === "undefined") return null;
  if (!hasAdsConsent()) {
    // Withdrawal (or rejection) invalidates stored attribution. Memory is
    // kept only while no choice has been made yet.
    if (readConsent()) pendingClick = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage blocked */
    }
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MetaClickId;
    if (!parsed?.expires || parsed.expires < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (!parsed.fbclid || !parsed.fbc) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * يقرأ الـURL الحاليّ ويحفظ `fbclid` إن وُجد.
 * نقرة أحدث تحلّ محلّ الأقدم (last-click كما تحتسبها Meta).
 */
export function captureMetaClickId(search?: string, capturedGen?: number): MetaClickId | null {
  if (typeof window === "undefined") return null;
  const gen = capturedGen ?? currentConsentGeneration();
  // A withdrawal after the landing was observed: never resurrect its click.
  if (adsWithdrawnSince(gen)) return getMetaClickId();
  const consent = readConsent();
  if (consent && !consent.ads) {
    clearMetaClickId();
    return null;
  }
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const fbclid = params.get("fbclid");
    if (!isValidFbclid(fbclid)) return getMetaClickId();
    const now = Date.now();
    const clean = fbclid.trim();
    const next: MetaClickId = {
      fbclid: clean,
      fbc: buildFbc(clean, now),
      clickAt: new Date(now).toISOString(),
      expires: now + TTL_MS,
    };
    if (!consent) {
      // No choice yet: memory only, nothing written to advertising storage.
      pendingClick = { ...next, gen };
      return null;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}

/**
 * سمات السلّة الخاصّة بـMeta (بادئة `_` = مخفيّة عن العميل في شوبيفاي).
 * `_fbp` يُرسَل دائماً إن وُجد الكوكي، حتّى بلا نقرة إعلان.
 */
export function buildMetaClickAttributes(): Array<{ key: string; value: string }> {
  const attrs: Array<{ key: string; value: string }> = [];
  if (typeof window === "undefined" || !hasAdsConsent()) return attrs;
  const click = getMetaClickId();
  const fbp = getFbp();
  if (click) {
    attrs.push({ key: "_fbclid", value: click.fbclid });
    attrs.push({ key: "_fbc", value: click.fbc });
    attrs.push({ key: "_meta_click_at", value: click.clickAt });
  }
  if (fbp) attrs.push({ key: "_fbp", value: fbp });
  return attrs;
}
